import { cardId, dealHands, findCommanderSeat, isRocket, legalPlays, parseCardId, sameCard, sortHand, taskEligibleCards, trickWinnerIndex } from './cards.js';
import { getMission, maxPlayersFor, taskCountFor } from './missions.js';
import { makeRng, shuffle } from './rng.js';
import { DEFAULT_HOUSE_RULES } from './types.js';
export class GameError extends Error {
    constructor(message) {
        super(message);
        this.name = 'GameError';
    }
}
export function setupGame(options) {
    const { missionId, playerIds, names, seed } = options;
    const attempt = options.attempt ?? 1;
    if (playerIds.length < 3 || playerIds.length > 7) {
        throw new GameError('This game needs 3 to 7 players.');
    }
    if (new Set(playerIds).size !== playerIds.length) {
        throw new GameError('Duplicate player in seat order.');
    }
    const mission = getMission(missionId);
    const cap = maxPlayersFor(mission);
    if (playerIds.length > cap) {
        throw new GameError(`${mission.name} needs every pilot to win a trick, so it caps at ${cap} players.`);
    }
    const rng = makeRng(seed + attempt * 7919);
    const hands = dealHands(playerIds.length, rng, (attempt - 1) % playerIds.length);
    const commanderSeat = findCommanderSeat(hands);
    const commanderId = playerIds[commanderSeat];
    const state = {
        missionId,
        seed,
        attempt,
        seats: playerIds.slice(),
        names: { ...names },
        houseRules: { ...DEFAULT_HOUSE_RULES, ...(options.houseRules ?? {}) },
        commanderId,
        players: {},
        tasks: buildTasks(mission, playerIds.length, rng),
        phase: 'draft',
        turnId: commanderId,
        trickNumber: 1,
        totalTricks: Math.ceil(40 / playerIds.length),
        currentTrick: [],
        trickLeaderId: commanderId,
        lastTrick: null,
        lastTrickWinnerId: null,
        completedTasks: 0,
        result: null,
        log: [],
        nextLogId: 1,
        draftTurnsTaken: 0
    };
    playerIds.forEach((id, seat) => {
        state.players[id] = {
            id,
            hand: hands[seat],
            communication: null,
            communicationUsed: false,
            tricksWon: 0
        };
    });
    log(state, 'info', `Mission ${mission.id}: ${mission.name} - attempt ${attempt}.`);
    log(state, 'info', `${nameOf(state, commanderId)} holds the 4 and takes command.`);
    log(state, 'info', 'Claim your tasks, then the commander leads the first trick.');
    return state;
}
function buildTasks(mission, players, rng) {
    const count = taskCountFor(mission, players);
    const cards = shuffle(taskEligibleCards(), rng).slice(0, count);
    const wantsFinal = mission.finalTask === true && count > 1;
    const availableForSequence = count - (wantsFinal ? 1 : 0);
    const orderedCount = mission.ordered === 'all'
        ? availableForSequence
        : Math.min(mission.ordered ?? 0, availableForSequence);
    return cards.map((card, i) => {
        let constraint = { kind: 'none' };
        if (i < orderedCount) {
            constraint = { kind: 'sequence', index: i + 1 };
        }
        else if (wantsFinal && i === count - 1) {
            constraint = { kind: 'final' };
        }
        return {
            id: `task-${cardId(card)}`,
            card,
            constraint,
            ownerId: null,
            completedAt: null
        };
    });
}
/* ------------------------------------------------------------------ */
/* Queries                                                             */
/* ------------------------------------------------------------------ */
export function nameOf(state, playerId) {
    return state.names[playerId] ?? 'Someone';
}
export function ledSuitOf(state) {
    const first = state.currentTrick[0];
    return first ? first.card.suit : null;
}
export function legalCardsFor(state, playerId) {
    const player = state.players[playerId];
    if (!player || state.phase !== 'playing' || state.turnId !== playerId)
        return [];
    return legalForHand(state, player.hand);
}
/** Follow-suit is the only restriction on what you may play. */
function legalForHand(state, hand) {
    return legalPlays(hand, ledSuitOf(state));
}
/** Colour cards that would form a truthful hint. */
export function communicableCardsFor(state, playerId) {
    const player = state.players[playerId];
    if (!player)
        return [];
    if (state.houseRules?.relaxedComms) {
        return sortHand(player.hand.filter((c) => !isRocket(c)));
    }
    const bySuit = new Map();
    for (const card of player.hand) {
        if (isRocket(card))
            continue;
        const list = bySuit.get(card.suit) ?? [];
        list.push(card);
        bySuit.set(card.suit, list);
    }
    const out = [];
    for (const list of bySuit.values()) {
        const sorted = list.slice().sort((a, b) => a.rank - b.rank);
        if (sorted.length === 1) {
            out.push(sorted[0]);
        }
        else {
            out.push(sorted[0], sorted[sorted.length - 1]);
        }
    }
    return sortHand(out);
}
/**
 * Truthful labels for a card. Under relaxed comms every colour card is
 * hintable, falling back to `have` when it is neither highest, lowest nor only.
 */
export function hintsForCard(hand, card, relaxed = false) {
    if (isRocket(card))
        return [];
    const sameSuit = hand.filter((c) => c.suit === card.suit).sort((a, b) => a.rank - b.rank);
    if (sameSuit.length === 0)
        return [];
    const hints = [];
    if (sameSuit.length === 1 && sameCard(sameSuit[0], card)) {
        hints.push('only');
    }
    else {
        if (sameCard(sameSuit[sameSuit.length - 1], card))
            hints.push('highest');
        if (sameCard(sameSuit[0], card))
            hints.push('lowest');
    }
    if (relaxed && hints.length === 0 && hand.some((c) => sameCard(c, card))) {
        hints.push('have');
    }
    return hints;
}
/** Returns null when the player may communicate, otherwise a reason to show. */
export function communicationBlockedReason(state, playerId) {
    const player = state.players[playerId];
    if (!player)
        return 'You are not in this mission.';
    const rules = getMission(state.missionId).rules ?? {};
    if (rules.noCommunication)
        return 'Comms are offline for this mission.';
    if (state.phase !== 'playing')
        return 'Comms open once the mission starts.';
    if (player.communicationUsed)
        return 'You already used your hint.';
    if (rules.communicationLockedUntilTrick && state.trickNumber < rules.communicationLockedUntilTrick) {
        return `Comms unlock at trick ${rules.communicationLockedUntilTrick}.`;
    }
    if (rules.communicationClosesAtTrick && state.trickNumber >= rules.communicationClosesAtTrick) {
        return 'The comms window has closed.';
    }
    // Last, so a permanent rule is never masked by a message that says to wait.
    if (state.currentTrick.length > 0)
        return 'Wait for this trick to finish.';
    if (communicableCardsFor(state, playerId).length === 0) {
        return 'No card in your hand qualifies as a hint.';
    }
    return null;
}
export function describeConstraint(constraint) {
    switch (constraint.kind) {
        case 'sequence':
            return `#${constraint.index}`;
        case 'final':
            return 'last';
        default:
            return '';
    }
}
/* ------------------------------------------------------------------ */
/* Actions                                                             */
/* ------------------------------------------------------------------ */
export function applyAction(state, action) {
    const next = structuredClone(state);
    switch (action.type) {
        case 'pickTask':
            pickTask(next, action.playerId, action.taskId);
            break;
        case 'endDraftTurn':
            endDraftTurn(next, action.playerId);
            break;
        case 'autoAssignTasks':
            autoAssignTasks(next, action.playerId);
            break;
        case 'communicate':
            communicate(next, action.playerId, action.cardId, action.hint);
            break;
        case 'playCard':
            playCard(next, action.playerId, action.cardId);
            break;
        default: {
            const never = action;
            throw new GameError(`Unknown action: ${JSON.stringify(never)}`);
        }
    }
    return next;
}
function requirePlayer(state, playerId) {
    const player = state.players[playerId];
    if (!player)
        throw new GameError('You are not seated in this mission.');
    return player;
}
function pickTask(state, playerId, taskId) {
    if (state.phase !== 'draft')
        throw new GameError('Tasks have already been claimed.');
    requirePlayer(state, playerId);
    if (state.turnId !== playerId)
        throw new GameError('It is not your turn to claim a task.');
    const task = state.tasks.find((t) => t.id === taskId);
    if (!task)
        throw new GameError('That task is not on the board.');
    // Clicking your own task again releases it, but only during your draft turn.
    if (task.ownerId === playerId) {
        task.ownerId = null;
        log(state, 'task', `${nameOf(state, playerId)} releases ${taskLabel(task)}.`);
        return;
    }
    if (task.ownerId)
        throw new GameError('That task is already claimed.');
    task.ownerId = playerId;
    log(state, 'task', `${nameOf(state, playerId)} claims ${taskLabel(task)}.`);
    // The turn stays put: you may take as many as you want before ending it.
    if (state.tasks.every((t) => t.ownerId))
        startFlight(state);
}
function endDraftTurn(state, playerId) {
    if (state.phase !== 'draft')
        throw new GameError('Tasks have already been claimed.');
    requirePlayer(state, playerId);
    if (state.turnId !== playerId)
        throw new GameError('It is not your turn to claim a task.');
    const took = state.tasks.some((t) => t.ownerId === playerId);
    log(state, 'task', took ? `${nameOf(state, playerId)} is done picking.` : `${nameOf(state, playerId)} takes nothing.`);
    advanceDraft(state);
}
function autoAssignTasks(state, playerId) {
    if (state.phase !== 'draft')
        throw new GameError('Tasks have already been claimed.');
    requirePlayer(state, playerId);
    if (state.commanderId !== playerId) {
        throw new GameError('Only the commander can deal the tasks out.');
    }
    if (state.turnId !== playerId) {
        throw new GameError('You can only do that on your turn to pick.');
    }
    log(state, 'task', `${nameOf(state, playerId)} let the computer deal the remaining tasks.`);
    dealRemaining(state, state.commanderId);
}
/** Hands every unclaimed task out, round-robin from `from`. */
function dealRemaining(state, from) {
    const rng = makeRng(state.seed + state.attempt * 104729 + 17);
    let seat = Math.max(0, state.seats.indexOf(from));
    const unclaimed = state.tasks.filter((t) => !t.ownerId);
    for (const task of shuffle(unclaimed, rng)) {
        const owner = state.seats[seat % state.seats.length];
        task.ownerId = owner;
        seat++;
    }
    // Only the ones just handed out, or the log repeats every earlier claim.
    for (const task of unclaimed) {
        log(state, 'task', `${nameOf(state, task.ownerId)} takes ${taskLabel(task)}.`);
    }
    startFlight(state);
}
/**
 * The draft is a single lap from the commander. Whatever nobody wanted lands on
 * the commander, so the table can never stall on an unclaimed task.
 */
function advanceDraft(state) {
    if (state.tasks.every((t) => t.ownerId)) {
        startFlight(state);
        return;
    }
    state.draftTurnsTaken++;
    if (state.draftTurnsTaken >= state.seats.length) {
        const left = state.tasks.filter((t) => !t.ownerId);
        for (const task of left) {
            task.ownerId = state.commanderId;
            log(state, 'task', `${nameOf(state, state.commanderId)} takes ${taskLabel(task)}.`);
        }
        log(state, 'task', `Everyone has picked, so ${nameOf(state, state.commanderId)} covers what is left.`);
        startFlight(state);
        return;
    }
    const commanderSeat = state.seats.indexOf(state.commanderId);
    const next = (commanderSeat + state.draftTurnsTaken) % state.seats.length;
    state.turnId = state.seats[next];
}
function startFlight(state) {
    state.phase = 'playing';
    state.turnId = state.commanderId;
    state.trickLeaderId = state.commanderId;
    log(state, 'info', `All tasks claimed. ${nameOf(state, state.commanderId)} leads.`);
}
function communicate(state, playerId, rawCardId, hint) {
    const player = requirePlayer(state, playerId);
    const blocked = communicationBlockedReason(state, playerId);
    if (blocked)
        throw new GameError(blocked);
    const card = parseCardId(rawCardId);
    if (!player.hand.some((c) => sameCard(c, card))) {
        throw new GameError('That card is not in your hand.');
    }
    if (isRocket(card))
        throw new GameError('Rockets cannot be used as hints.');
    if (!hintsForCard(player.hand, card, state.houseRules?.relaxedComms).includes(hint)) {
        throw new GameError('That hint would not be truthful.');
    }
    player.communication = { card, hint, trick: state.trickNumber, played: false };
    player.communicationUsed = true;
    log(state, 'hint', `${nameOf(state, playerId)} signals ${card.suit} ${card.rank} as their ${hintWord(hint)}.`);
}
function hintWord(hint) {
    if (hint === 'only')
        return `only card of that colour`;
    if (hint === 'have')
        return `card in hand`;
    return `${hint} card of that colour`;
}
function playCard(state, playerId, rawCardId) {
    if (state.phase !== 'playing')
        throw new GameError('The mission is not in flight.');
    const player = requirePlayer(state, playerId);
    if (state.turnId !== playerId)
        throw new GameError('It is not your turn.');
    const card = parseCardId(rawCardId);
    const index = player.hand.findIndex((c) => sameCard(c, card));
    if (index < 0)
        throw new GameError('That card is not in your hand.');
    const legal = legalForHand(state, player.hand);
    if (!legal.some((c) => sameCard(c, card))) {
        throw new GameError(`You must follow ${ledSuitOf(state)}.`);
    }
    player.hand.splice(index, 1);
    state.currentTrick.push({ playerId, card });
    if (player.communication && sameCard(player.communication.card, card)) {
        player.communication.played = true;
    }
    log(state, 'play', `${nameOf(state, playerId)} plays ${card.suit} ${card.rank}.`);
    const everyoneActed = state.seats.every((id) => state.currentTrick.some((p) => p.playerId === id) ||
        state.players[id].hand.length === 0);
    if (everyoneActed) {
        resolveTrick(state);
    }
    else {
        state.turnId = nextActiveSeat(state, playerId);
    }
}
function nextActiveSeat(state, fromId) {
    const start = state.seats.indexOf(fromId);
    for (let step = 1; step <= state.seats.length; step++) {
        const id = state.seats[(start + step) % state.seats.length];
        const alreadyPlayed = state.currentTrick.some((p) => p.playerId === id);
        if (!alreadyPlayed && state.players[id].hand.length > 0)
            return id;
    }
    return fromId;
}
function resolveTrick(state) {
    const plays = state.currentTrick.slice();
    const winner = plays[trickWinnerIndex(plays.map((p) => p.card))];
    const winnerId = winner.playerId;
    const mission = getMission(state.missionId);
    const rules = mission.rules ?? {};
    state.players[winnerId].tricksWon += 1;
    state.lastTrick = { number: state.trickNumber, plays, winnerId };
    log(state, 'trick', `${nameOf(state, winnerId)} takes trick ${state.trickNumber}.`);
    if (rules.commanderWinsFirstTrick && state.trickNumber === 1 && winnerId !== state.commanderId) {
        fail(state, `The commander had to win the opening trick, but ${nameOf(state, winnerId)} took it.`);
        return;
    }
    if (rules.commanderWinsNoTricks && winnerId === state.commanderId) {
        fail(state, 'The commander was not allowed to win a trick on this mission.');
        return;
    }
    if (rules.noBackToBackTricks && state.lastTrickWinnerId === winnerId) {
        fail(state, `${nameOf(state, winnerId)} won two tricks in a row, which this mission forbids.`);
        return;
    }
    if (rules.maxTricksPerPlayer !== undefined &&
        state.players[winnerId].tricksWon > rules.maxTricksPerPlayer) {
        fail(state, `${nameOf(state, winnerId)} won more than ${rules.maxTricksPerPlayer} trick${rules.maxTricksPerPlayer === 1 ? '' : 's'}, which this mission forbids.`);
        return;
    }
    if (!resolveTasks(state, plays, winnerId))
        return;
    state.lastTrickWinnerId = winnerId;
    state.currentTrick = [];
    state.trickNumber += 1;
    const everyoneScored = !rules.everyoneWinsATrick ||
        state.seats.every((id) => state.players[id].tricksWon > 0);
    if (state.tasks.every((t) => t.completedAt !== null) && everyoneScored) {
        state.phase = 'won';
        state.result = { outcome: 'won', reason: 'Every task completed. Mission accomplished.' };
        log(state, 'win', 'Every task completed. Mission accomplished!');
        return;
    }
    const cardsLeft = state.seats.some((id) => state.players[id].hand.length > 0);
    if (!cardsLeft) {
        const open = state.tasks.filter((t) => t.completedAt === null).length;
        if (open === 0 && !everyoneScored) {
            fail(state, 'Every task was completed, but not every pilot won a trick.');
        }
        else {
            fail(state, `Out of cards with ${open} task${open === 1 ? '' : 's'} still open.`);
        }
        return;
    }
    const leader = state.players[winnerId].hand.length > 0
        ? winnerId
        : nextActiveSeat(state, winnerId);
    state.trickLeaderId = leader;
    state.turnId = leader;
}
/** Returns false when the trick blew the mission. */
function resolveTasks(state, plays, winnerId) {
    const captured = state.tasks.filter((t) => t.completedAt === null && plays.some((p) => sameCard(p.card, t.card)));
    if (captured.length === 0)
        return true;
    // Resolve in the only order that can possibly satisfy the constraints.
    captured.sort((a, b) => constraintWeight(a) - constraintWeight(b));
    for (const task of captured) {
        if (task.ownerId !== winnerId) {
            fail(state, `${nameOf(state, winnerId)} captured ${taskLabel(task)}, which belongs to ${nameOf(state, task.ownerId)}.`);
            return false;
        }
        if (task.constraint.kind === 'sequence') {
            const blocking = state.tasks.find((t) => t.constraint.kind === 'sequence' &&
                t.constraint.index < task.constraint.index &&
                t.completedAt === null);
            if (blocking) {
                fail(state, `${taskLabel(task)} was completed out of order - ${taskLabel(blocking)} had to come first.`);
                return false;
            }
        }
        if (task.constraint.kind === 'final') {
            const outstanding = state.tasks.filter((t) => t.id !== task.id && t.completedAt === null);
            if (outstanding.length > 0) {
                fail(state, `${taskLabel(task)} had to be the final task, but others were still open.`);
                return false;
            }
        }
        state.completedTasks += 1;
        task.completedAt = state.completedTasks;
        log(state, 'task', `${nameOf(state, winnerId)} completed ${taskLabel(task)}.`);
    }
    return true;
}
function constraintWeight(task) {
    switch (task.constraint.kind) {
        case 'sequence':
            return task.constraint.index;
        case 'none':
            return 1000;
        case 'final':
            return 2000;
    }
}
function fail(state, reason) {
    state.phase = 'lost';
    state.result = { outcome: 'lost', reason };
    log(state, 'fail', reason);
}
function taskLabel(task) {
    const tag = describeConstraint(task.constraint);
    return `${task.card.suit} ${task.card.rank}${tag ? ` [${tag}]` : ''}`;
}
function log(state, kind, text) {
    state.log.push({ id: state.nextLogId++, trick: state.trickNumber, kind, text });
    if (state.log.length > 300)
        state.log.splice(0, state.log.length - 300);
}
/* ------------------------------------------------------------------ */
/* Views                                                               */
/* ------------------------------------------------------------------ */
/** `connected` comes from the transport layer; the engine itself has no sockets. */
export function viewFor(state, viewerId, connected = {}) {
    const players = state.seats.map((id) => {
        const p = state.players[id];
        return {
            id,
            handCount: p.hand.length,
            // Keep the historical marker internally, not as an active table hint.
            communication: p.communication?.played ? null : p.communication,
            communicationUsed: p.communicationUsed,
            tricksWon: p.tricksWon,
            isCommander: id === state.commanderId,
            isTurn: id === state.turnId,
            connected: connected[id] ?? true
        };
    });
    const me = viewerId ? state.players[viewerId] : undefined;
    return {
        missionId: state.missionId,
        mission: getMission(state.missionId),
        attempt: state.attempt,
        phase: state.phase,
        houseRules: state.houseRules ?? DEFAULT_HOUSE_RULES,
        seats: state.seats.slice(),
        commanderId: state.commanderId,
        turnId: state.turnId,
        trickNumber: state.trickNumber,
        totalTricks: state.totalTricks,
        currentTrick: state.currentTrick.slice(),
        trickLeaderId: state.trickLeaderId,
        ledSuit: ledSuitOf(state),
        lastTrick: state.lastTrick,
        tasks: state.tasks.map((t) => ({ ...t })),
        players,
        completedTasks: state.completedTasks,
        draftTurnsTaken: state.draftTurnsTaken,
        result: state.result,
        log: state.log.slice(-120),
        you: me && viewerId
            ? {
                id: viewerId,
                hand: sortHand(me.hand),
                legalCardIds: legalCardsFor(state, viewerId).map(cardId),
                communicableCardIds: communicableCardsFor(state, viewerId).map(cardId),
                canCommunicate: communicationBlockedReason(state, viewerId) === null,
                communicationBlockedReason: communicationBlockedReason(state, viewerId)
            }
            : null
    };
}
//# sourceMappingURL=engine.js.map